// ============================================
// CUSTOM HOOKS
// ============================================

import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import toast from 'react-hot-toast';
import { i18n } from '@crew-at-sea/i18n';
import { useTranslation } from '@crew-at-sea/i18n';
import { useAuthStore } from '@/store/auth';
import {
  authApi,
  usersApi,
  jobsApi,
  applicationsApi,
  certificatesApi,
  seaServiceApi,
  documentsApi,
  favoritesApi,
  companiesApi,
  reviewsApi,
  notificationsApi,
  careerApi,
  matchingApi,
  supportApi,
  getApi,
} from '@/services/api';
import type { LoginRequest } from '@crew-at-sea/shared-types';
import type { SupportRequest } from '@crew-at-sea/api-client';

// i18n kısayol
const t = (key: string) => i18n.t(key);

// ============================================
// AUTH HOOKS
// ============================================

export function useLogin() {
  const { login } = useAuthStore();

  return useMutation({
    mutationFn: async (data: LoginRequest) => {
      try {
        return await authApi.login(data);
      } catch (error: any) {
        const msgStr = error?.message || '';
        let friendly = '';
        const lower = msgStr.toLowerCase();
        
        if (lower.includes('invalid') || lower.includes('unauthorized') || lower.includes('incorrect') || lower.includes('wrong')) {
          friendly = t('auth:login.errors.invalidCredentials');
        } else if (lower.includes('suspended')) {
          friendly = t('auth:login.errors.accountSuspended');
        } else if (lower.includes('inactive')) {
          friendly = t('auth:login.errors.accountInactive');
        } else if (lower.includes('not found')) {
          friendly = t('auth:login.errors.userNotFound');
        } else {
          friendly = msgStr;
        }

        // Fallback if translations are missing
        if (friendly === 'auth:login.errors.invalidCredentials') friendly = 'Kullanıcı bilgileri hatalı. Lütfen e-posta ve şifrenizi kontrol edin.';
        if (friendly === 'auth:login.errors.accountInactive') friendly = 'Hesabınız aktif değil. E-posta doğrulamanızı kontrol edin.';
        if (friendly === 'auth:login.errors.accountSuspended') friendly = 'Hesabınız askıya alınmış. Destek için iletişime geçin.';
        if (friendly === 'auth:login.errors.userNotFound') friendly = 'Bu e-posta adresiyle kayıtlı hesap bulunamadı.';

        throw new Error(friendly);
      }
    },
    onSuccess: (response: any) => {
      const result = response.data || response;
      const { user, accessToken, refreshToken } = result;
      
      if (!user) {
        throw new Error(t('panel:hooks.auth.invalidResponse'));
      }
      
      const role = user.role;
      if (!role) {
        throw new Error(t('panel:hooks.auth.roleNotFound'));
      }
      
      if (role === 'COMPANY') {
        const company = user.company || null;
        const isEmailVerified = !!user.emailVerified;
        
        if (!isEmailVerified) {
          throw new Error('Giriş engellendi: Hesabınıza erişebilmek için e-posta doğrulamanızın yapılmış olması gerekmektedir.');
        }

        const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const targetHost = isLocal ? 'http://localhost:3003' : 'https://company.crewatsea.com';
        
        toast.success('Şirket girişi başarılı, yönlendiriliyorsunuz...');
        setTimeout(() => {
          window.location.href = `${targetHost}/login-callback?token=${accessToken}&refresh=${refreshToken}`;
        }, 100);
        return;
      }
      
      if (role !== 'SEAFARER') {
        throw new Error(t('panel:hooks.auth.roleNotFound'));
      }
      
      const seafarer = user.seafarer || null;
      
      const isEmailVerified = !!user.emailVerified;
      if (!isEmailVerified) {
        throw new Error('Giriş engellendi: Hesabınıza erişebilmek için e-posta doğrulamanızın yapılmış olması gerekmektedir.');
      }

      // Önce token'ları kaydet
      login(
        { id: user.id, email: user.email, role: 'SEAFARER', seafarer },
        accessToken,
        refreshToken
      );
      
      toast.success(t('panel:hooks.auth.loginSuccess'));
      
      // window.location.href kullan - TanStack Router navigate bazen çalışmıyor
      setTimeout(() => {
        window.location.href = '/';
      }, 100);
    },
  });
}

export function useGoogleLogin() {
  const { login } = useAuthStore();

  return useMutation({
    mutationFn: (data: { credential: string; role?: string }) => authApi.googleLogin(data),
    onSuccess: (response: any) => {
      const result = response.data || response;
      const { user, accessToken, refreshToken } = result;
      
      if (!user) {
        throw new Error(t('panel:hooks.auth.invalidResponse'));
      }
      
      const role = user.role;
      if (!role) {
        throw new Error(t('panel:hooks.auth.roleNotFound'));
      }
      
      if (role === 'COMPANY') {
        const company = user.company || null;
        const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const targetHost = isLocal ? 'http://localhost:3003' : 'https://company.crewatsea.com';
        
        toast.success('Şirket girişi başarılı, yönlendiriliyorsunuz...');
        setTimeout(() => {
          window.location.href = `${targetHost}/login-callback?token=${accessToken}&refresh=${refreshToken}`;
        }, 100);
        return;
      }
      
      if (role !== 'SEAFARER') {
        throw new Error(t('panel:hooks.auth.seafarerOnly'));
      }
      
      const seafarer = user.seafarer || null;

      login(
        { id: user.id, email: user.email, role: 'SEAFARER', seafarer },
        accessToken,
        refreshToken
      );
      
      toast.success(t('panel:hooks.auth.loginSuccess'));
      
      setTimeout(() => {
        window.location.href = '/';
      }, 100);
    },
  });
}

export function useRegister() {
  const { login } = useAuthStore();

  return useMutation({
    mutationFn: (data: any) => authApi.registerSeafarer(data),
    onSuccess: (response: any) => {
      const result = response.data || response;
      const { user, accessToken, refreshToken } = result;
      
      if (!user) {
        throw new Error(t('panel:hooks.auth.invalidResponse'));
      }
      
      toast.success('Kayıt başarılı! Lütfen giriş yapmadan önce e-posta adresinize gönderilen doğrulama linkine tıklayın.', { duration: 6000 });
      
      // window.location.href kullan
      setTimeout(() => {
        window.location.href = '/login';
      }, 100);
    },
  });
}

export function useLogout() {
  const navigate = useNavigate();
  const { logout, refreshToken } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => authApi.logout(refreshToken || undefined),
    onSettled: () => {
      logout();
      queryClient.clear();
      toast.success(t('panel:hooks.auth.logoutSuccess'));
      navigate({ to: '/login' });
    },
  });
}

// ============================================
// PROFILE HOOKS
// ============================================

export function useMyProfile() {
  return useQuery({
    queryKey: ['profile', 'me'],
    queryFn: () => usersApi.getMyProfile(),
  });
}

export function useMyStats() {
  return useQuery({
    queryKey: ['profile', 'stats'],
    queryFn: () => usersApi.getMyStats(),
    staleTime: 1000 * 60 * 2,
    retry: false, // Hata gelince anında dur, skeleton takılmasın
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const { updateUser } = useAuthStore();
  
  return useMutation({
    mutationFn: (data: any) => usersApi.updateMyProfile(data),
    onSuccess: (response: any) => {
      const result = response.data || response;
      // Only update minimal user info in store (not the entire profile)
      if (result.firstName || result.lastName) {
        updateUser({ firstName: result.firstName, lastName: result.lastName });
      }
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      
    },
  });
}

export function useUploadAvatar() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (file: File) => usersApi.uploadAvatar(file),
    onSuccess: (data) => {
      // Force refetch profile data
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      // Also invalidate the specific profile/me query
      queryClient.refetchQueries({ queryKey: ['profile', 'me'] });
      // NOT: Toast profil sayfasında gösteriliyor, burada tekrar gösterme
    },
  });
}

export function useDeleteAvatar() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: () => usersApi.deleteAvatar(),
    onSuccess: () => {
      // Force refetch profile data
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.refetchQueries({ queryKey: ['profile', 'me'] });
      toast.success(t('panel:hooks.profile.avatarDeleted'));
    },
  });
}

export function useProfileViews(days?: number) {
  return useQuery({
    queryKey: ['profile', 'views', days],
    queryFn: () => usersApi.getProfileViews(days),
  });
}

export function useUpdatePreferences() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (preferences: any) => usersApi.updatePreferences(preferences),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      
    },
  });
}

export function useUpdateVisibility() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (visibility: { isPublic: boolean }) => usersApi.updateVisibility(visibility),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      toast.success(variables.isPublic ? t('panel:hooks.profile.visibilityPublic') : t('panel:hooks.profile.visibilityPrivate'));
    },
  });
}

export function useDeleteAccount() {
  const { logout } = useAuthStore();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: () => usersApi.deleteAccount(),
    onSuccess: () => {
      logout();
      queryClient.clear();
      toast.success('Hesabınız başarıyla silindi.');
      setTimeout(() => {
        window.location.href = '/login';
      }, 100);
    },
  });
}

// ============================================
// JOB HOOKS
// ============================================

export function useJobs(params?: any) {
  return useQuery({
    queryKey: ['jobs', params],
    queryFn: () => jobsApi.list(params),
  });
}

export function useJob(slug: string) {
  return useQuery({
    queryKey: ['jobs', slug],
    queryFn: () => jobsApi.getBySlug(slug),
    enabled: !!slug,
  });
}

export function useRecommendedJobs(params?: any) {
  return useQuery({
    queryKey: ['jobs', 'recommended', params],
    queryFn: () => jobsApi.getRecommended(params),
    staleTime: 1000 * 60 * 2,
    retry: false,
  });
}

export function useSimilarJobs(slug: string, limit?: number) {
  return useQuery({
    queryKey: ['jobs', slug, 'similar'],
    queryFn: () => jobsApi.getSimilar(slug, limit),
    enabled: !!slug,
  });
}

// ============================================
// APPLICATION HOOKS
// ============================================

export function useMyApplications() {
  return useQuery({
    queryKey: ['applications', 'me'],
    queryFn: () => applicationsApi.getMyApplications(),
  });
}

export function useApplyToJob() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: any) => applicationsApi.apply(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applications'] });
      queryClient.invalidateQueries({ queryKey: ['profile', 'stats'] });
      toast.success(t('panel:hooks.application.applied'));
    },
  });
}

export function useWithdrawApplication() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => applicationsApi.withdraw(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applications'] });
      queryClient.invalidateQueries({ queryKey: ['profile', 'stats'] });
      toast.success(t('panel:hooks.application.withdrawn'));
    },
  });
}

// 12.3 Get Application Detail
export function useApplication(id: string) {
  return useQuery({
    queryKey: ['applications', id],
    queryFn: () => applicationsApi.getById(id),
    enabled: !!id,
  });
}

// [KULLANICI-H7] Check if user can apply to a job
export function useCheckCanApply(jobId: string) {
  return useQuery({
    queryKey: ['applications', 'can-apply', jobId],
    queryFn: () => applicationsApi.checkCanApply(jobId),
    enabled: !!jobId,
    staleTime: 30000, // 30 saniye cache
    retry: false,
  });
}

// ============================================
// CERTIFICATE HOOKS
// ============================================

export function useMyCertificates() {
  return useQuery({
    queryKey: ['certificates', 'me'],
    queryFn: () => certificatesApi.getMyCertificates(),
  });
}

export function useCertificateTypes(category?: string) {
  return useQuery({
    queryKey: ['certificates', 'types', category],
    queryFn: () => certificatesApi.getTypes(category),
  });
}

export function useExpiringCertificates(days?: number) {
  return useQuery({
    queryKey: ['certificates', 'expiring', days],
    queryFn: () => certificatesApi.getExpiring(days),
  });
}

export function useCreateCertificate() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: any) => certificatesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certificates'] });
      toast.success(t('panel:hooks.certificate.created'));
    },
  });
}

export function useUpdateCertificate() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      certificatesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certificates'] });
      toast.success(t('panel:hooks.certificate.updated'));
    },
  });
}

export function useDeleteCertificate() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => certificatesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certificates'] });
      toast.success(t('panel:hooks.certificate.deleted'));
    },
  });
}

// 12.1 Certificate Type by ID
export function useCertificateType(id: string) {
  return useQuery({
    queryKey: ['certificates', 'types', id],
    queryFn: () => certificatesApi.getTypeById(id),
    enabled: !!id,
  });
}

// 12.1 Upload Certificate Document
export function useUploadCertificateDocument() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, file, onProgress }: { id: string; file: File; onProgress?: (percent: number) => void }) =>
      certificatesApi.uploadDocument(id, file, onProgress),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certificates'] });
      toast.success(t('panel:hooks.certificate.documentUploaded'));
    },
  });
}

// 12.1 Missing Certificates for career progression
export function useMissingCertificates() {
  return useQuery({
    queryKey: ['certificates', 'missing'],
    queryFn: () => certificatesApi.getMissing(),
  });
}

// ============================================
// SEA SERVICE HOOKS
// ============================================

export function useMySeaService() {
  return useQuery({
    queryKey: ['sea-service', 'me'],
    queryFn: () => seaServiceApi.getMyRecords(),
  });
}

export function useSeaServiceSummary() {
  return useQuery({
    queryKey: ['sea-service', 'summary'],
    queryFn: () => seaServiceApi.getSummary(),
  });
}

export function useCreateSeaService() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: any) => seaServiceApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sea-service'] });
      toast.success(t('panel:hooks.seaService.created'));
    },
  });
}

export function useUpdateSeaService() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      seaServiceApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sea-service'] });
      toast.success(t('panel:hooks.seaService.updated'));
    },
  });
}

export function useDeleteSeaService() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => seaServiceApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sea-service'] });
      toast.success(t('panel:hooks.seaService.deleted'));
    },
  });
}

// 12.2 Upload Sea Service Document
export function useUploadSeaServiceDocument() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, file, onProgress }: { id: string; file: File; onProgress?: (percent: number) => void }) =>
      seaServiceApi.uploadDocument(id, file, onProgress),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sea-service'] });
      toast.success(t('panel:hooks.seaService.documentUploaded'));
    },
  });
}

// ============================================
// DOCUMENT HOOKS
// ============================================

export function useMyDocuments() {
  return useQuery({
    queryKey: ['documents', 'me'],
    queryFn: () => documentsApi.getMyDocuments(),
  });
}

export function useDocumentFolders() {
  return useQuery({
    queryKey: ['documents', 'folders'],
    queryFn: () => documentsApi.getFolders(),
  });
}

export function useExpiringDocuments(days: number = 30) {
  return useQuery({
    queryKey: ['documents', 'expiring', days],
    queryFn: async () => {
      const result = await getApi().get<any>(`/documents/expiring?days=${days}`);
      return (result as any)?.data || result;
    },
  });
}

export function useCreateDocument() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: {
      name: string;
      type: string;
      fileUrl: string;
      fileSize?: number;
      mimeType?: string;
      folderId?: string;
      expiryDate?: string;
    }) => documentsApi.create(data as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast.success(t('panel:hooks.document.created'));
    },
  });
}

export function useUpdateDocument() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; type?: string; expiryDate?: string } }) =>
      documentsApi.update(id, data as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast.success(t('panel:hooks.document.updated'));
    },
  });
}

export function useDownloadDocument() {
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await documentsApi.getDownloadUrl(id);
      const result = (response as any)?.data || response;
      // Open download URL in new tab - backend returns 'url', not 'downloadUrl'
      const downloadUrl = result?.url || result?.downloadUrl;
      if (downloadUrl) {
        window.open(downloadUrl, '_blank');
      }
      return result;
    },
  });
}

export function useMoveDocument() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, folderId }: { id: string; folderId: string | null }) =>
      documentsApi.move(id, { folderId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast.success(t('panel:hooks.document.moved'));
    },
  });
}

export function useCreateFolder() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: { name: string; color?: string }) =>
      documentsApi.createFolder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', 'folders'] });
      toast.success(t('panel:hooks.folder.created'));
    },
  });
}

export function useUpdateFolder() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; color?: string } }) =>
      documentsApi.updateFolder(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', 'folders'] });
      toast.success(t('panel:hooks.folder.updated'));
    },
  });
}

export function useDeleteFolder() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => documentsApi.deleteFolder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', 'folders'] });
      queryClient.invalidateQueries({ queryKey: ['documents', 'me'] });
      toast.success(t('panel:hooks.folder.deleted'));
    },
  });
}

export function useDeleteDocument() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => documentsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast.success(t('panel:hooks.document.deleted'));
    },
  });
}

// ============================================
// FAVORITE HOOKS
// ============================================

export function useFavoriteJobs() {
  return useQuery({
    queryKey: ['favorites', 'jobs'],
    queryFn: () => favoritesApi.getFavoriteJobs(),
  });
}

export function useAddFavoriteJob() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (jobId: string) => favoritesApi.addFavoriteJob({ jobId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites', 'jobs'] });
      toast.success(t('panel:hooks.favorite.added'));
    },
  });
}

export function useRemoveFavoriteJob() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (jobId: string) => favoritesApi.removeFavoriteJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites', 'jobs'] });
      toast.success(t('panel:hooks.favorite.removed'));
    },
  });
}

// 12.5 Update Favorite Job Notes
export function useUpdateFavoriteJob() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ jobId, notes }: { jobId: string; notes?: string }) =>
      favoritesApi.updateFavoriteJob(jobId, { notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites', 'jobs'] });
      toast.success(t('panel:hooks.favorite.noteSaved'));
    },
  });
}

// ============================================
// COMPANY HOOKS
// ============================================

export function useCompanies(params?: any) {
  return useQuery({
    queryKey: ['companies', params],
    queryFn: () => companiesApi.list(params),
  });
}

export function useCompany(slug: string) {
  return useQuery({
    queryKey: ['companies', slug],
    queryFn: () => companiesApi.getBySlug(slug),
    enabled: !!slug,
  });
}

export function useCompanyJobs(slug: string, params?: any) {
  return useQuery({
    queryKey: ['companies', slug, 'jobs', params],
    queryFn: () => companiesApi.getJobs(slug, params),
    enabled: !!slug,
  });
}

// 12.8 Company Reviews by Slug
export function useCompanyReviewsBySlug(slug: string, params?: any) {
  return useQuery({
    queryKey: ['companies', slug, 'reviews', params],
    queryFn: () => companiesApi.getReviews(slug, params),
    enabled: !!slug,
  });
}

// ============================================
// REVIEW HOOKS
// ============================================

export function useMyReviews() {
  return useQuery({
    queryKey: ['reviews', 'me'],
    queryFn: () => reviewsApi.getMyReviews(),
  });
}

export function useCompanyReviews(companyId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['reviews', 'company', companyId],
    queryFn: () => reviewsApi.getCompanyReviews(companyId),
    enabled: options?.enabled !== undefined ? options.enabled : !!companyId,
  });
}

export function useCreateReview() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: any) => reviewsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      toast.success(t('panel:hooks.review.created'));
    },
  });
}

// 12.6 Company Review Summary
export function useCompanyReviewSummary(companyId: string) {
  return useQuery({
    queryKey: ['reviews', 'company', companyId, 'summary'],
    queryFn: () => reviewsApi.getReviewSummary(companyId),
    enabled: !!companyId,
  });
}

// 12.6 Update Review
export function useUpdateReview() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      reviewsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      toast.success(t('panel:hooks.review.updated'));
    },
  });
}

// 12.6 Delete Review
export function useDeleteReview() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => reviewsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      toast.success(t('panel:hooks.review.deleted'));
    },
  });
}

// ============================================
// NOTIFICATION HOOKS
// ============================================

export function useNotifications() {
  return useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationsApi.getNotifications(),
  });
}

export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => notificationsApi.getUnreadCount(),
    refetchInterval: 30000, // Poll every 30 seconds
  });
}

export function useMarkNotificationAsRead() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => notificationsApi.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMarkAllNotificationsAsRead() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: () => notificationsApi.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      toast.success(t('panel:hooks.notification.allMarkedRead'));
    },
  });
}

export function useDeleteNotification() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => notificationsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useNotificationSettings() {
  return useQuery({
    queryKey: ['notifications', 'settings'],
    queryFn: () => notificationsApi.getSettings(),
  });
}

export function useUpdateNotificationSettings() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: any) => notificationsApi.updateSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', 'settings'] });
      toast.success(t('panel:hooks.notification.settingsUpdated'));
    },
  });
}

// ============================================
// CAREER HOOKS
// ============================================

export function useRanks(department?: string) {
  return useQuery({
    queryKey: ['career', 'ranks', department],
    queryFn: () => careerApi.getRanks(department),
  });
}

export function useRank(id: string) {
  return useQuery({
    queryKey: ['career', 'ranks', id],
    queryFn: () => careerApi.getRankById(id),
    enabled: !!id,
  });
}

export function useCareerPaths(department?: string) {
  return useQuery({
    queryKey: ['career', 'paths', department],
    queryFn: () => careerApi.getCareerPaths(department),
  });
}

export function useCareerPath(id: string) {
  return useQuery({
    queryKey: ['career', 'paths', id],
    queryFn: () => careerApi.getCareerPathById(id),
    enabled: !!id,
  });
}

export function useMarketInsights(department?: string) {
  return useQuery({
    queryKey: ['career', 'market-insights', department],
    queryFn: async () => {
      const result = await getApi().get<any>(`/career/market-insights${department ? `?department=${department}` : ''}`);
      return (result as any)?.data || result;
    },
  });
}

export function useMyCareerProgress() {
  return useQuery({
    queryKey: ['career', 'me', 'progress'],
    queryFn: () => careerApi.getMyProgress(),
  });
}

export function useMyNextSteps() {
  return useQuery({
    queryKey: ['career', 'me', 'next-steps'],
    queryFn: () => careerApi.getNextSteps(),
  });
}

export function useSetCareerPath() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: { careerPathId: string; currentRankId: string }) =>
      careerApi.setCareerPath(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['career'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      toast.success(t('panel:hooks.career.pathSet'));
    },
  });
}

// ============================================
// MATCHING HOOKS
// ============================================

export function useMatchingJobs() {
  return useQuery({
    queryKey: ['matching', 'jobs'],
    queryFn: () => matchingApi.getMatchingJobs(),
    staleTime: 1000 * 60 * 2,
    retry: false,
  });
}

export function useJobMatchScore(jobId: string) {
  return useQuery({
    queryKey: ['matching', 'jobs', jobId, 'score'],
    queryFn: () => matchingApi.getJobMatchScore(jobId),
    enabled: !!jobId,
  });
}

// ============================================
// UI HOOKS
// ============================================

export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

export function usePagination(initialPage: number = 1, initialLimit: number = 10) {
  const [page, setPage] = useState(initialPage);
  const [limit, setLimit] = useState(initialLimit);

  const nextPage = useCallback(() => setPage((p) => p + 1), []);
  const prevPage = useCallback(() => setPage((p) => Math.max(1, p - 1)), []);
  const goToPage = useCallback((p: number) => setPage(p), []);
  const changeLimit = useCallback((l: number) => {
    setLimit(l);
    setPage(1);
  }, []);

  return {
    page,
    limit,
    nextPage,
    prevPage,
    goToPage,
    changeLimit,
  };
}

export function useModal(initialState: boolean = false) {
  const [isOpen, setIsOpen] = useState(initialState);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  return { isOpen, open, close, toggle };
}

// ============================================
// SUPPORT HOOKS
// ============================================

export function useSendSupportRequest() {
  return useMutation({
    mutationFn: (data: SupportRequest) => supportApi.sendRequest(data),
    onSuccess: () => {
      toast.success(t('panel:hooks.support.sent'));
    },
  });
}

// ============================================
// FOLLOW COMPANY HOOKS
// ============================================

export function useFollowedCompanies() {
  return useQuery({
    queryKey: ['follow', 'companies'],
    queryFn: async () => {
      const result = await getApi().get<any>('/favorites/companies');
      return (result as any)?.data || result;
    },
  });
}

export function useIsFollowingCompany(companyId: string) {
  return useQuery({
    queryKey: ['follow', 'companies', companyId],
    queryFn: async () => {
      if (!companyId) return false;
      const result = await getApi().get<any>(`/favorites/companies/${companyId}/status`);
      return (result as any)?.data || result;
    },
    enabled: !!companyId,
  });
}

export function useFollowCompany() {
  const queryClient = useQueryClient();
  const { t } = useTranslation(['panel']);
  return useMutation({ 
    mutationFn: async (companyId: string) => {
      const result = await getApi().post<any>(`/favorites/companies/${companyId}`);
      return (result as any)?.data || result;
    },
    onSuccess: (_, companyId) => {
      queryClient.setQueryData(['follow', 'companies', companyId], true);
      queryClient.invalidateQueries({ queryKey: ['follow', 'companies'] });
      toast.success(t('panel:hooks.follow.followed'));
    },
  });
}

export function useUnfollowCompany() {
  const queryClient = useQueryClient();
  const { t } = useTranslation(['panel']);
  return useMutation({
    mutationFn: async (companyId: string) => {
      const result = await getApi().delete<any>(`/favorites/companies/${companyId}`);
      return (result as any)?.data || result;
    },
    onSuccess: (_, companyId) => {
      queryClient.setQueryData(['follow', 'companies', companyId], false);
      queryClient.invalidateQueries({ queryKey: ['follow', 'companies'] });
      toast.success(t('panel:hooks.follow.unfollowed'));
    },
  });
}
